import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth-context'
import {
  Button,
  Input,
  Label,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Skeleton,
} from '@lead-magnifier/ui'
import { Building2, Mail, UserPlus, CheckCircle2, XCircle, Clock } from 'lucide-react'

export const Route = createFileRoute('/invite/$token/')({
  component: InvitePage,
})

interface Invitation {
  id: string
  email: string
  role: 'admin' | 'user'
  status: string
  expiresAt: string
  tenantName: string
  tenantId: string
}

function InvitePage() {
  const { token } = Route.useParams()
  const { user, signIn, signUp } = useAuth()
  const navigate = useNavigate()

  const [mode, setMode] = useState<'view' | 'login' | 'register'>('view')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [error, setError] = useState('')
  const [authLoading, setAuthLoading] = useState(false)

  // Fetch invitation details
  const { data: invitation, isLoading, error: fetchError } = useQuery({
    queryKey: ['invitation', token],
    queryFn: async (): Promise<Invitation | null> => {
      const { data, error } = await supabase
        .from('invitations')
        .select(`
          id,
          email,
          role,
          status,
          expires_at,
          tenants(id, name)
        `)
        .eq('token', token)
        .single()

      if (error || !data) return null

      const tenant = data.tenants as { id: string; name: string }
      return {
        id: data.id,
        email: data.email,
        role: data.role,
        status: data.status,
        expiresAt: data.expires_at,
        tenantName: tenant?.name || 'Unbekannt',
        tenantId: tenant?.id || '',
      }
    },
  })

  // Accept invitation mutation
  const acceptInvitation = useMutation({
    mutationFn: async () => {
      if (!user || !invitation) throw new Error('Nicht authentifiziert')

      // Check if user email matches invitation
      if (user.email?.toLowerCase() !== invitation.email.toLowerCase()) {
        throw new Error(`Diese Einladung wurde an ${invitation.email} gesendet. Bitte melde dich mit diesem Account an.`)
      }

      // Add user to tenant
      const { error: memberError } = await supabase
        .from('tenant_users')
        .insert({
          tenant_id: invitation.tenantId,
          user_id: user.id,
          role: invitation.role,
        })

      if (memberError) {
        if (memberError.code === '23505') {
          // Already a member, just mark invitation as accepted
        } else {
          throw memberError
        }
      }

      // Mark invitation as accepted
      const { error: updateError } = await supabase
        .from('invitations')
        .update({
          status: 'accepted',
          accepted_at: new Date().toISOString(),
        })
        .eq('id', invitation.id)

      if (updateError) throw updateError

      return true
    },
    onSuccess: () => {
      // Navigate to the workspace
      navigate({ to: '/dashboard' })
    },
  })

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setAuthLoading(true)

    try {
      await signIn(email, password)
      // After login, user state will update and they can accept
      setMode('view')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Anmeldung fehlgeschlagen')
    } finally {
      setAuthLoading(false)
    }
  }

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setAuthLoading(true)

    try {
      await signUp(email, password, name)
      // After registration, user state will update
      setMode('view')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registrierung fehlgeschlagen')
    } finally {
      setAuthLoading(false)
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <Skeleton className="h-12 w-12 rounded-full mx-auto mb-4" />
            <Skeleton className="h-6 w-48 mx-auto mb-2" />
            <Skeleton className="h-4 w-64 mx-auto" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-24 w-full" />
          </CardContent>
        </Card>
      </div>
    )
  }

  // Invalid or expired invitation
  if (!invitation || fetchError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <XCircle className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle>Einladung ungültig</CardTitle>
            <CardDescription>
              Diese Einladung existiert nicht oder wurde bereits verwendet.
            </CardDescription>
          </CardHeader>
          <CardFooter className="justify-center">
            <Link to="/login">
              <Button variant="outline">Zur Anmeldung</Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    )
  }

  // Check if expired
  const isExpired = new Date(invitation.expiresAt) < new Date()
  if (isExpired || invitation.status !== 'pending') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <Clock className="h-6 w-6 text-muted-foreground" />
            </div>
            <CardTitle>Einladung abgelaufen</CardTitle>
            <CardDescription>
              Diese Einladung ist nicht mehr gültig. Bitte fordere eine neue Einladung an.
            </CardDescription>
          </CardHeader>
          <CardFooter className="justify-center">
            <Link to="/login">
              <Button variant="outline">Zur Anmeldung</Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    )
  }

  // Login form
  if (mode === 'login') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Anmelden</CardTitle>
            <CardDescription>
              Melde dich an, um die Einladung anzunehmen
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleLogin}>
            <CardContent className="space-y-4">
              {error && (
                <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={invitation.email}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Passwort</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
            </CardContent>
            <CardFooter className="flex flex-col space-y-2">
              <Button type="submit" className="w-full" disabled={authLoading}>
                {authLoading ? 'Wird angemeldet...' : 'Anmelden'}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setMode('view')}>
                Zurück
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    )
  }

  // Register form
  if (mode === 'register') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Account erstellen</CardTitle>
            <CardDescription>
              Erstelle einen Account, um {invitation.tenantName} beizutreten
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleRegister}>
            <CardContent className="space-y-4">
              {error && (
                <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Max Mustermann"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail</Label>
                <Input
                  id="email"
                  type="email"
                  value={email || invitation.email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Verwende die E-Mail-Adresse, an die die Einladung gesendet wurde.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Passwort</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Mindestens 8 Zeichen"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                />
              </div>
            </CardContent>
            <CardFooter className="flex flex-col space-y-2">
              <Button type="submit" className="w-full" disabled={authLoading}>
                {authLoading ? 'Wird erstellt...' : 'Account erstellen'}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setMode('view')}>
                Zurück
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    )
  }

  // Main view - show invitation and accept button
  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Building2 className="h-6 w-6 text-primary" />
          </div>
          <CardTitle>Einladung zu {invitation.tenantName}</CardTitle>
          <CardDescription>
            Du wurdest eingeladen, {invitation.tenantName} als{' '}
            <strong>{invitation.role === 'admin' ? 'Admin' : 'Aussteller'}</strong> beizutreten.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="rounded-lg border p-4 space-y-3">
            <div className="flex items-center gap-3">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Eingeladen als</p>
                <p className="text-sm text-muted-foreground">{invitation.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <UserPlus className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Rolle</p>
                <p className="text-sm text-muted-foreground">
                  {invitation.role === 'admin' ? 'Admin' : 'Aussteller'}
                </p>
              </div>
            </div>
          </div>

          {acceptInvitation.error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {acceptInvitation.error instanceof Error
                ? acceptInvitation.error.message
                : 'Einladung konnte nicht angenommen werden'}
            </div>
          )}
        </CardContent>

        <CardFooter className="flex flex-col space-y-3">
          {user ? (
            <>
              {user.email?.toLowerCase() === invitation.email.toLowerCase() ? (
                <Button
                  className="w-full"
                  onClick={() => acceptInvitation.mutate()}
                  disabled={acceptInvitation.isPending}
                >
                  {acceptInvitation.isPending ? (
                    'Wird angenommen...'
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Einladung annehmen
                    </>
                  )}
                </Button>
              ) : (
                <div className="text-center space-y-2">
                  <p className="text-sm text-muted-foreground">
                    Du bist als <strong>{user.email}</strong> angemeldet.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Diese Einladung wurde an <strong>{invitation.email}</strong> gesendet.
                  </p>
                  <Button variant="outline" onClick={() => setMode('login')}>
                    Mit anderem Account anmelden
                  </Button>
                </div>
              )}
            </>
          ) : (
            <>
              <Button className="w-full" onClick={() => {
                setEmail(invitation.email)
                setMode('register')
              }}>
                <UserPlus className="h-4 w-4 mr-2" />
                Account erstellen
              </Button>
              <Button variant="outline" className="w-full" onClick={() => {
                setEmail(invitation.email)
                setMode('login')
              }}>
                Ich habe bereits einen Account
              </Button>
            </>
          )}
        </CardFooter>
      </Card>
    </div>
  )
}
